/*#
# perlin-noise

**Seeded 3D Perlin noise.** The same seed always gives the same field, which
is what terrain, clouds and everything else procedural here is built on.

```javascript
import { PerlinNoise } from 'tosijs-3d/perlin-noise'

const noise = new PerlinNoise(1234) // always pass a seed
noise.noise3D(x, y, z) // about -1..1, smooth
noise.noise2D(x, y)
noise.fractal(x, y, z, 6, 0.5, 2) // octaves, persistence, lacunarity
```

Leave the seed out and you get a different field every time, which is rarely
what you want. Noise is 0 at every whole-number coordinate, so scale your
inputs (`x * 0.01`) and do not sample on the integer grid.
*/
/*{ "parent": "reference", "order": 20 }*/

/**
 * A clean implementation of 3D Perlin Noise
 * Inspired by Ken Perlin's improved noise algorithm
 */
export class PerlinNoise {
  private perm: Uint8Array
  /**
   * Gradient vectors, FLAT: `[x0,y0,z0, x1,y1,z1, …]`, 512 × 3 doubles.
   *
   * This was `Array<[number, number, number]>` — 512 little boxed tuple arrays — and
   * `grad()` destructured one per call. `grad()` runs EIGHT times per `noise3D` (one per
   * cube corner), `noise3D` runs 7× per terrain height sample, and a terrain tile takes
   * 729 samples: that destructure-from-a-pointer-chase was the hottest line in the
   * library, and V8 suffers far more from it than JavaScriptCore does.
   *
   * Flat `Float64Array` keeps the values BIT-IDENTICAL (doubles in, doubles out — a
   * Float32Array would silently change the terrain) while making a corner lookup three
   * contiguous loads with no indirection.
   */
  private gradP: Float64Array

  /**
   * Creates a new Perlin noise generator
   * @param seed Optional seed for the random number generator
   */
  constructor(seed?: number) {
    // Initialize permutation table
    this.perm = new Uint8Array(512)
    this.gradP = new Float64Array(512 * 3)

    // `??`, not `||`: seed 0 is a seed, and used to get a random field.
    this.seed(seed ?? Math.random() * 65536)
  }

  /**
   * Seeds the noise generator
   * @param seed Numeric seed value
   */
  public seed(seed: number): void {
    // Create a simple random number generator based on the seed
    const random = (): number => {
      // Simple xorshift algorithm
      seed ^= seed << 13
      seed ^= seed >> 17
      seed ^= seed << 5
      return Math.abs(seed) / 2147483647
    }

    // Create shuffled permutation table
    const p = new Uint8Array(256)

    // Fill with values 0...255
    for (let i = 0; i < 256; i++) {
      p[i] = i
    }

    // Fisher-Yates shuffle with our seeded random
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(random() * (i + 1))
      ;[p[i], p[j]] = [p[j], p[i]] // Swap values
    }

    // Duplicate to avoid buffer overruns
    for (let i = 0; i < 512; i++) {
      this.perm[i] = p[i & 255]
      const [gx, gy, gz] = this.generateGradient(this.perm[i])
      this.gradP[i * 3] = gx
      this.gradP[i * 3 + 1] = gy
      this.gradP[i * 3 + 2] = gz
    }
  }

  /**
   * Samples 3D Perlin noise at the given coordinates
   * @param x X coordinate
   * @param y Y coordinate
   * @param z Z coordinate
   * @returns Value between -1 and 1
   */
  public noise3D(x: number, y: number, z: number): number {
    // Find unit cube that contains point
    // Floor ONCE per axis and reuse it for both the cell index and the fractional part
    // (it used to be computed twice per axis, six Math.floor calls where three will do).
    const fx = Math.floor(x)
    const fy = Math.floor(y)
    const fz = Math.floor(z)
    const X = fx & 255
    const Y = fy & 255
    const Z = fz & 255

    // Find relative position in cube
    x -= fx
    y -= fy
    z -= fz

    // Compute fade curves
    const u = this.fade(x)
    const v = this.fade(y)
    const w = this.fade(z)

    // Hash coordinates of the 8 cube corners
    const A = this.perm[X] + Y
    const AA = this.perm[A] + Z
    const AB = this.perm[A + 1] + Z
    const B = this.perm[X + 1] + Y
    const BA = this.perm[B] + Z
    const BB = this.perm[B + 1] + Z

    // Calculate noise contributions from each corner
    return this.lerp(
      this.lerp(
        this.lerp(
          this.grad(this.perm[AA], x, y, z),
          this.grad(this.perm[BA], x - 1, y, z),
          u
        ),
        this.lerp(
          this.grad(this.perm[AB], x, y - 1, z),
          this.grad(this.perm[BB], x - 1, y - 1, z),
          u
        ),
        v
      ),
      this.lerp(
        this.lerp(
          this.grad(this.perm[AA + 1], x, y, z - 1),
          this.grad(this.perm[BA + 1], x - 1, y, z - 1),
          u
        ),
        this.lerp(
          this.grad(this.perm[AB + 1], x, y - 1, z - 1),
          this.grad(this.perm[BB + 1], x - 1, y - 1, z - 1),
          u
        ),
        v
      ),
      w
    )
  }

  /**
   * Samples 2D Perlin noise at the given coordinates
   * @param x X coordinate
   * @param y Y coordinate
   * @returns Value between -1 and 1
   */
  public noise2D(x: number, y: number): number {
    // For convenience, we can use the 3D function and set z to 0
    return this.noise3D(x, y, 0)
  }

  /**
   * Generates multiple octaves of noise at the given coordinates
   * @param x X coordinate
   * @param y Y coordinate
   * @param z Z coordinate
   * @param octaves Number of octaves to generate
   * @param persistence How much each octave contributes to the overall shape (amplitude multiplier)
   * @param lacunarity Frequency multiplier for successive octaves
   * @returns Value typically between -1 and 1 (but can exceed these bounds with high octave count)
   */
  public fractal(
    x: number,
    y: number,
    z: number,
    octaves = 6,
    persistence = 0.5,
    lacunarity = 2.0
  ): number {
    let total = 0
    let frequency = 1
    let amplitude = 1
    let maxValue = 0 // Used for normalizing result

    for (let i = 0; i < octaves; i++) {
      total +=
        this.noise3D(x * frequency, y * frequency, z * frequency) * amplitude

      maxValue += amplitude

      amplitude *= persistence
      frequency *= lacunarity
    }

    // Normalize the result
    return total / maxValue
  }

  /**
   * Internal helper: Fade function as defined by Ken Perlin
   * 6t^5 - 15t^4 + 10t^3 is the smoothstep function that provides smooth interpolation
   */
  private fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10)
  }

  /**
   * Internal helper: Linear interpolation
   */
  private lerp(a: number, b: number, t: number): number {
    return (1 - t) * a + t * b
  }

  /**
   * Internal helper: Compute gradient direction
   * This is the core of Perlin noise - calculate dot product between
   * gradient vectors and distance vectors
   */
  private grad(hash: number, x: number, y: number, z: number): number {
    // Three contiguous loads, no indirection and nothing to destructure — see `gradP`.
    const g = this.gradP
    const i = (hash & 511) * 3
    return g[i] * x + g[i + 1] * y + g[i + 2] * z
  }

  /**
   * Internal helper: Generate a random gradient vector
   */
  private generateGradient(hash: number): [number, number, number] {
    // Convert low 4 bits of hash to 0-15
    const h = hash & 15

    // Create gradients that are uniformly distributed on a sphere
    // This is crucial for proper 3D noise
    const u = h < 8 ? 1 : -1
    const v = h < 4 ? 1 : h === 12 || h === 14 ? 1 : -1
    const w = h & 1 ? 1 : -1

    // Use different gradient combinations based on hash value
    switch (h & 7) {
      case 0:
        return [u, v, 0]
      case 1:
        return [-v, u, 0]
      case 2:
        return [0, u, v]
      case 3:
        return [0, -v, u]
      case 4:
        return [u, 0, v]
      case 5:
        return [-v, 0, u]
      case 6:
        return [v, 0, u]
      case 7:
        return [u, v, w]
      default:
        return [0, 0, 0] // Should never happen
    }
  }
}
